import * as React from 'react'
import { createPortal } from 'react-dom'
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import type { BaseSelection, LexicalEditor } from 'lexical'
import {
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'

import { getSelectedNode } from '../utils/get-selected-node'
import { setFloatingElemPositionForLinkEditor } from '../utils/set-floating-elem-position-for-link-editor'
import { sanitizeUrl } from '../utils/url'
import { Button } from '@repo/design-system-cf/components/button'
import { IconCheck, IconDelete } from '@repo/design-system-cf/components/icons'

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor
  isLink: boolean
  setIsLink: React.Dispatch<boolean>
  anchorElem: HTMLElement
  isLinkEditMode: boolean
  setIsLinkEditMode: React.Dispatch<boolean>
}): React.JSX.Element {
  const editorRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [linkUrl, setLinkUrl] = React.useState('')
  const [editedLinkUrl, setEditedLinkUrl] = React.useState('')
  const [lastSelection, setLastSelection] = React.useState<BaseSelection | null>(null)

  const updateLinkEditor = React.useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const linkParent = $findMatchingParent(node, $isLinkNode)

      let url = ''

      if (linkParent) {
        url = linkParent.getURL()
      } else if ($isLinkNode(node)) {
        url = node.getURL()
      }

      setLinkUrl(url)

      // IMPORTANT: set edited value from fresh URL, not state
      if (isLinkEditMode) {
        setEditedLinkUrl(url)
      }
    }
    const editorElem = editorRef.current
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (editorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect()
      if (domRect) {
        domRect.y += 40
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem)
      }
      setLastSelection(selection)
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem)
      }
      setLastSelection(null)
      setIsLinkEditMode(false)
      setLinkUrl('')
    }

    return true
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode])

  React.useEffect(() => {
    const scrollerElem = anchorElem.parentElement

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor()
      })
    }

    window.addEventListener('resize', update)

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update)
      }
    }
  }, [anchorElem.parentElement, editor, updateLinkEditor])

  React.useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
    )
  }, [editor, updateLinkEditor, setIsLink, isLink])

  React.useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor()
    })
  }, [editor, updateLinkEditor])

  React.useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isLinkEditMode, isLink])

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isLinkEditMode) {
        return
      }
      if (!editorRef.current) {
        return
      }

      const target = event.target as HTMLElement

      if (target.closest('[data-link-trigger]')) {
        return
      }
      if (!editorRef.current.contains(target)) {
        setEditedLinkUrl('')
        setIsLinkEditMode(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isLinkEditMode, setIsLinkEditMode])

  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleLinkSubmission()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setIsLinkEditMode(false)
    }
  }

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (editedLinkUrl !== '') {
        const sanitizedUrl = sanitizeUrl(editedLinkUrl)
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizedUrl)
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent()
            if ($isAutoLinkNode(parent)) {
              const url = parent.getURL()
              const linkNode = $createLinkNode(url, {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              })
              parent.replace(linkNode, true)
            }
            $setSelection(null)
          }
        })
      }
      setEditedLinkUrl('')
      setIsLinkEditMode(false)
    }
  }

  return (
    <div
      ref={editorRef}
      className="rounded-2 absolute bg-cf-white left-0 top-0 max-w-[400px] bg-white shadow-cf-elevation-light-1 z-10"
    >
      {isLinkEditMode ? (
        <div className="flex p-2 gap-2">
          <input
            ref={inputRef}
            className="rounded outline-none ring-0 border border-cf-transparent-grey-1000-12
             hover:border-cf-transparent-grey-1000-20 focus-within:!border-cf-transparent-grey-1000-52 px-2 py-1 w-[300px] text-14 link-input bg-cf-white"
            value={editedLinkUrl}
            placeholder={isLink ? '' : 'Add URL'}
            onChange={e => setEditedLinkUrl(e.target.value)}
            onKeyDown={monitorInputInteraction}
          />
          <Button
            type="button"
            variant="primary"
            onClick={() => setIsLinkEditMode(true)}
            iconLeft={<IconCheck />}
          />
        </div>
      ) : // oxlint-disable-next-line unicorn/no-nested-ternary
      isLink ? (
        <div className="flex p-2 gap-2">
          <input
            ref={inputRef}
            className="rounded outline-none ring-0 border border-cf-transparent-grey-1000-12 
            hover:border-cf-transparent-grey-1000-20 focus-within:!border-cf-transparent-grey-1000-52 px-2 py-1 w-[300px] text-14 link-input  bg-cf-white"
            value={linkUrl}
            placeholder={isLink ? '' : 'Add URL'}
            onChange={e => setEditedLinkUrl(e.target.value)}
            onKeyDown={monitorInputInteraction}
          />
          {isLink ? (
            <Button
              type="button"
              variant="tertiary"
              iconLeft={<IconDelete />}
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
                setIsLinkEditMode(false)
              }}
            />
          ) : null}
          <Button
            type="button"
            variant="primary"
            iconLeft={<IconCheck />}
            onClick={() => setIsLinkEditMode(true)}
          />
        </div>
      ) : null}
    </div>
  )
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: React.Dispatch<boolean>,
): React.JSX.Element | null {
  const [activeEditor, setActiveEditor] = React.useState(editor)
  const [isLink, setIsLink] = React.useState(false)

  React.useEffect(() => {
    function updateToolbar() {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection)
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode)
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode)
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false)
          return
        }
        const badNode = selection.getNodes().find(node => {
          const linkNode = $findMatchingParent(node, $isLinkNode)
          const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode)
          // oxlint-disable-next-line array-callback-return
          if (
            !linkNode?.is(focusLinkNode) &&
            !autoLinkNode?.is(focusAutoLinkNode) &&
            !linkNode &&
            !autoLinkNode &&
            !$isLineBreakNode(node)
          ) {
            return node
          }
        })
        // oxlint-disable-next-line no-negated-condition
        if (!badNode) {
          setIsLink(true)
        } else {
          setIsLink(false)
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar()
          setActiveEditor(newEditor)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        payload => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection)
            const linkNode = $findMatchingParent(node, $isLinkNode)
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank')
              return true
            }
          }
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
    )
  }, [editor])

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  )
}

export function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement
  isLinkEditMode: boolean
  setIsLinkEditMode: React.Dispatch<boolean>
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode)
}
